import React, { useState, useEffect, useRef } from 'react';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { Sensor, Obstacle } from '../../api/models';
import SensorDefaultsModal from './SensorDefaultsModal';
import { formatInputNumber, toDegrees, toRadians } from '../../utils/formatting';
import { alignmentStatusColor } from '../../utils/sensorStatusSphere';
import { alignedPoseDiffersFromLive, hasAlignedPoseSnapshot } from '../../utils/alignedPose';
import { useUserStore, getAvailableSensorTypes } from '../../state/userStore';
import { useSensorPoseEditSession } from '../../hooks/useSensorPoseEditSession';

const PropertiesPanel: React.FC = () => {
  const selectedObjectId = useSceneStore((state: SceneState) => state.selectedObjectId);
  const setSelectedObjectId = useSceneStore((state: SceneState) => state.setSelectedObjectId);
  const sensors = useSceneStore((state: SceneState) => state.sensors);
  const obstacles = useSceneStore((state: SceneState) => state.obstacles);
  const updateSensor = useSceneStore((state: SceneState) => state.updateSensor);
  const updateObstacleProperties = useSceneStore(
    (state: SceneState) => state.updateObstacleProperties
  );
  const addObstacle = useSceneStore((state: SceneState) => state.addObstacle);
  const addSensor = useSceneStore((state: SceneState) => state.addSensor);
  const deleteSensor = useSceneStore((state: SceneState) => state.deleteSensor);
  const deleteObstacle = useSceneStore((state: SceneState) => state.deleteObstacle);
  const restoreSensorToAligned = useSceneStore((state: SceneState) => state.restoreSensorToAligned);
  const [isDefaultsModalOpen, setIsDefaultsModalOpen] = useState(false);
  const selectedSensorType = useSceneStore((state: SceneState) => state.selectedSensorType);
  const lastAddedSensorId = useSceneStore((state: SceneState) => state.lastAddedSensorId);
  const setLastAddedSensorId = useSceneStore((state: SceneState) => state.setLastAddedSensorId);
  const referenceImageAlignMode = useSceneStore((state: SceneState) => state.referenceImageAlignMode);
  const setReferenceImageAlignMode = useSceneStore((state: SceneState) => state.setReferenceImageAlignMode);
  const transformMode = useSceneStore((state: SceneState) => state.transformMode);
  const setTransformMode = useSceneStore((state: SceneState) => state.setTransformMode);
  const pushSensorPoseHistory = useSceneStore((state: SceneState) => state.pushSensorPoseHistory);
  const { beginSensorPoseEdit, finishSensorPoseEdit } = useSensorPoseEditSession(pushSensorPoseHistory);

  const selectedSensor = sensors.find((s: Sensor) => s.id === selectedObjectId);
  const selectedObstacle = obstacles.find((o: Obstacle) => o.id === selectedObjectId);
  const isReferenceImageSelected = selectedObjectId === 'reference-image';
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedObjectId !== 'reference-image' && referenceImageAlignMode !== null) {
      setReferenceImageAlignMode(null);
    }
  }, [selectedObjectId, referenceImageAlignMode, setReferenceImageAlignMode]);

  useEffect(() => {
    if (lastAddedSensorId && selectedObjectId === lastAddedSensorId && nameInputRef.current) {
      nameInputRef.current.focus();
      setLastAddedSensorId(null);
    }
  }, [selectedObjectId, lastAddedSensorId, setLastAddedSensorId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.key === 'Delete') {
        if (selectedSensor) {
          deleteSensor(selectedSensor.id);
        } else if (selectedObstacle) {
          deleteObstacle(selectedObstacle.id);
        }
      } else if (event.key === 'Escape') {
        if (selectedObjectId) {
          setSelectedObjectId(null);
        }
      } else if (event.key.toLowerCase() === 't') {
        setTransformMode('translate');
      } else if (event.key.toLowerCase() === 'r') {
        setTransformMode('rotate');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedSensor, selectedObstacle, deleteSensor, deleteObstacle, selectedObjectId, setSelectedObjectId, setTransformMode]);

  const panelStyle: React.CSSProperties = {
    background: 'rgba(25, 35, 45, 0.8)',
    color: 'white',
    padding: '15px',
    borderRadius: '8px',
    fontFamily: 'sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: '0 0 auto',
    minHeight: 0,
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const inputStyle: React.CSSProperties = {
    width: '120px',
    background: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '4px',
    padding: '4px 8px',
  };

  const buttonStyle: React.CSSProperties = {
    background: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    marginTop: '10px',
  };

  const warningBannerStyle: React.CSSProperties = {
    background: 'rgba(245, 158, 11, 0.15)',
    border: '1px solid #f59e0b',
    borderRadius: '6px',
    color: '#fde68a',
    fontSize: '12px',
    lineHeight: 1.45,
    padding: '8px 10px',
  };

  const alignmentBadgeStyle: React.CSSProperties = {
    color: alignmentStatusColor(selectedSensor?.alignmentStatus),
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'lowercase',
  };

  const handleSensorPropertyChange = (
    prop: 'position' | 'rotation',
    axisIndex: number,
    value: string
  ) => {
    if (!selectedSensor) return;
    let newValue = parseFloat(value) || 0;
    if (prop === 'rotation') {
      newValue = toRadians(newValue);
    }
    const newPropValue = [...selectedSensor[prop]] as [number, number, number];
    newPropValue[axisIndex] = newValue;
    updateSensor(selectedSensor.id, { [prop]: newPropValue });
  };

  const handleStringPropertyChange = (prop: 'name' | 'type', value: string) => {
    if (!selectedSensor) return;
    if (prop === 'type') {
      const updates: Partial<Sensor> = { type: value };
      if (value === 'Rotating' && (!selectedSensor.parameters || selectedSensor.type !== 'Rotating')) {
        updates.parameters = {
          minVerticalAngle: -15,
          maxVerticalAngle: 15,
          verticalChannels: 16,
          horizontalResolution: 2000,
          horizontalFov: 360,
          effectiveRange: 100
        };
      } else if (value === 'MEMS' && (!selectedSensor.parameters || selectedSensor.type !== 'MEMS')) {
        updates.parameters = {
          minVerticalAngle: -12.5,
          maxVerticalAngle: 12.5,
          verticalChannels: 250,
          horizontalResolution: 1280,
          horizontalFov: 120,
          effectiveRange: 250
        };
      }
      updateSensor(selectedSensor.id, updates);
    } else {
      updateSensor(selectedSensor.id, { [prop]: value });
    }
  };

  const handleNumberPropertyChange = (prop: 'rotationRate', value: string) => {
    if (!selectedSensor) return;
    const newValue = parseInt(value, 10);
    if (!isNaN(newValue)) {
      updateSensor(selectedSensor.id, { [prop]: newValue });
    }
  };

  const handleDelete = () => {
    if (selectedSensor) {
      deleteSensor(selectedSensor.id);
    } else if (selectedObstacle) {
      deleteObstacle(selectedObstacle.id);
    }
  };

  const handleObstaclePropertyChange = (
    prop: 'position' | 'rotation' | 'size',
    axisIndex: number,
    value: string
  ) => {
    if (!selectedObstacle) return;
    let newValue = parseFloat(value) || 0;
    if (prop === 'rotation') {
      newValue = toRadians(newValue);
    }
    const newPropValue = [...selectedObstacle[prop]] as [number, number, number];
    newPropValue[axisIndex] = newValue;
    updateObstacleProperties(selectedObstacle.id, { [prop]: newPropValue });
  };

  const advancedSensorsEnabled = useUserStore((state) => state.hasFeature('advanced_sensors'));
  const lidarTypes = getAvailableSensorTypes(advancedSensorsEnabled);

  return (
    <div style={panelStyle}>
      <div style={{ marginBottom: '15px' }}>
        <h5 style={{ marginTop: 0, marginBottom: '10px' }}>Transform Mode</h5>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setTransformMode('translate')}
            style={{
              ...buttonStyle,
              width: 'auto',
              flex: 1,
              marginTop: 0,
              background: transformMode === 'translate' ? '#10b981' : '#6b7280'
            }}
            title="Move (Shortcut: T)"
          >
            Move (T)
          </button>
          <button
            onClick={() => setTransformMode('rotate')}
            style={{
              ...buttonStyle,
              width: 'auto',
              flex: 1,
              marginTop: 0,
              background: transformMode === 'rotate' ? '#10b981' : '#6b7280'
            }}
            title="Rotate (Shortcut: R)"
          >
            Rotate (R)
          </button>
        </div>
      </div>

      <hr style={{ width: '100%', border: '1px solid #475569', margin: '0 0 10px 0' }} />

      {!selectedSensor && !selectedObstacle && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p>No object selected</p>
        </div>
      )}

      {selectedSensor && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <h4 style={{ margin: 0 }}>{selectedSensor.name}</h4>
              {selectedSensor.pcdUrl && (
                <span
                  data-testid="properties-alignment-status"
                  style={alignmentBadgeStyle}
                >
                  {selectedSensor.alignmentStatus || 'unaligned'}
                </span>
              )}
            </div>
            <button onClick={handleDelete} style={{ ...buttonStyle, background: '#ef4444', marginTop: 0 }}>
              Delete
            </button>
          </div>
          {selectedSensor.pcdUrl && selectedSensor.alignmentStatus === 'aligned' && (
            <div
              data-testid="properties-aligned-edit-warning"
              style={warningBannerStyle}
            >
              This sensor is aligned. Changing position or orientation will mark it unaligned until you re-apply alignment in Align mode.
            </div>
          )}
          {selectedSensor.pcdUrl && hasAlignedPoseSnapshot(selectedSensor) && alignedPoseDiffersFromLive(selectedSensor) && (
            <button
              type="button"
              data-testid="properties-restore-aligned"
              style={{ ...buttonStyle, marginTop: 0, background: '#334155' }}
              onClick={() => restoreSensorToAligned(selectedSensor.id)}
            >
              Restore Aligned Pose
            </button>
          )}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Name:
              <input
                ref={nameInputRef}
                type="text"
                value={selectedSensor.name}
                onChange={(e) => handleStringPropertyChange('name', e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Type:
              <select
                value={selectedSensor.type}
                onChange={(e) => handleStringPropertyChange('type', e.target.value)}
                style={inputStyle}
              >
                {lidarTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Rotation Rate (Hz):
              <input
                type="number"
                value={selectedSensor.rotationRate}
                onChange={(e) =>
                  handleNumberPropertyChange('rotationRate', e.target.value)
                }
                style={inputStyle}
              />
            </label>
          </div>
          <div style={inputGroupStyle}>
            <strong>Position (m)</strong>
            {['X', 'Y', 'Z'].map((axis, index) => (
              <label style={labelStyle} key={axis}>
                {axis}:
                <input
                  type="number"
                  step={0.1}
                  value={formatInputNumber(selectedSensor.position[index])}
                  onFocus={() => beginSensorPoseEdit(selectedSensor.id, `position:${index}`)}
                  onBlur={finishSensorPoseEdit}
                  onChange={(e) =>
                    handleSensorPropertyChange('position', index, e.target.value)
                  }
                  style={{ ...inputStyle, width: '60px' }}
                />
              </label>
            ))}
          </div>
          <div style={inputGroupStyle}>
            <strong>Orientation (deg)</strong>
            {['X', 'Y', 'Z'].map((axis, index) => (
              <label style={labelStyle} key={axis}>
                {axis}:
                <input
                  type="number"
                  step={1}
                  value={formatInputNumber(toDegrees(selectedSensor.rotation[index]))}
                  onFocus={() => beginSensorPoseEdit(selectedSensor.id, `rotation:${index}`)}
                  onBlur={finishSensorPoseEdit}
                  onChange={(e) =>
                    handleSensorPropertyChange('rotation', index, e.target.value)
                  }
                  style={{ ...inputStyle, width: '60px' }}
                />
              </label>
            ))}
          </div>
          {(selectedSensor.type === 'Rotating' || selectedSensor.type === 'MEMS') && (
            <div style={inputGroupStyle}>
              <strong>{selectedSensor.type === 'MEMS' ? 'MEMS Sensor Parameters' : 'Rotating Sensor Parameters'}</strong>
              <label style={labelStyle}>
                Min Vertical Angle (deg):
                <input
                  type="number"
                  step={0.1}
                  value={selectedSensor.parameters?.minVerticalAngle ?? (selectedSensor.type === 'MEMS' ? -12.5 : -15)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, minVerticalAngle: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Max Vertical Angle (deg):
                <input
                  type="number"
                  step={0.1}
                  value={selectedSensor.parameters?.maxVerticalAngle ?? (selectedSensor.type === 'MEMS' ? 12.5 : 15)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, maxVerticalAngle: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Vertical Channels:
                <input
                  type="number"
                  step={1}
                  value={selectedSensor.parameters?.verticalChannels ?? (selectedSensor.type === 'MEMS' ? 250 : 16)}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, verticalChannels: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Horizontal Res ({selectedSensor.type === 'MEMS' ? 'firings/line' : 'firings/frame'}):
                <input
                  type="number"
                  step={100}
                  value={selectedSensor.parameters?.horizontalResolution ?? (selectedSensor.type === 'MEMS' ? 1280 : 2000)}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, horizontalResolution: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Horizontal FOV (deg):
                <input
                  type="number"
                  step={1}
                  value={selectedSensor.parameters?.horizontalFov ?? (selectedSensor.type === 'MEMS' ? 120 : 360)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, horizontalFov: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Effective Range (m):
                <input
                  type="number"
                  step={1}
                  value={selectedSensor.parameters?.effectiveRange ?? (selectedSensor.type === 'MEMS' ? 250 : 100)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    updateSensor(selectedSensor.id, {
                      parameters: { ...selectedSensor.parameters, effectiveRange: val }
                    });
                  }}
                  style={inputStyle}
                />
              </label>
            </div>
          )}
        </>
      )}

      {selectedObstacle && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>Obstacle Properties</h4>
            <button onClick={handleDelete} style={{ ...buttonStyle, background: '#ef4444', marginTop: 0 }}>
              Delete
            </button>
          </div>
          <div style={inputGroupStyle}>
            <strong>Position (m)</strong>
            {['X', 'Y', 'Z'].map((axis, index) => (
              <label style={labelStyle} key={axis}>
                {axis}:
                <input
                  type="number"
                  step={0.1}
                  value={formatInputNumber(selectedObstacle.position[index])}
                  onChange={(e) =>
                    handleObstaclePropertyChange('position', index, e.target.value)
                  }
                  style={{ ...inputStyle, width: '60px' }}
                />
              </label>
            ))}
          </div>
          <div style={inputGroupStyle}>
            <strong>Orientation (deg)</strong>
            {['X', 'Y', 'Z'].map((axis, index) => (
              <label style={labelStyle} key={axis}>
                {axis}:
                <input
                  type="number"
                  step={1}
                  value={formatInputNumber(toDegrees(selectedObstacle.rotation[index]))}
                  onChange={(e) =>
                    handleObstaclePropertyChange('rotation', index, e.target.value)
                  }
                  style={{ ...inputStyle, width: '60px' }}
                />
              </label>
            ))}
          </div>
          <div style={inputGroupStyle}>
            <strong>Size (m)</strong>
            {['Width (X)', 'Depth (Y)', 'Height (Z)'].map((axis, index) => (
              <label style={labelStyle} key={axis}>
                {axis}:
                <input
                  type="number"
                  step={0.1}
                  value={formatInputNumber(selectedObstacle.size[index])}
                  onChange={(e) =>
                    handleObstaclePropertyChange('size', index, e.target.value)
                  }
                  style={{ ...inputStyle, width: '60px' }}
                />
              </label>
            ))}
          </div>
        </>
      )}

      <hr style={{ width: '100%', border: '1px solid #475569' }} />

      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
        <div style={{ display: 'flex', flex: 1 }}>
          <button
            onClick={() => addSensor(selectedSensorType)}
            style={{ ...buttonStyle, flex: 1, margin: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            Add {selectedSensorType}
          </button>
            <button
                onClick={() => setIsDefaultsModalOpen(true)}
                style={{ ...buttonStyle, margin: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '8px' }}
                title="Sensor Defaults"
            >
                ⚙️
            </button>
        </div>
        <button onClick={() => addObstacle()} style={{ ...buttonStyle, flex: 1, margin: 0 }}>
          Add Obstacle
        </button>
      </div>
      <SensorDefaultsModal
        isOpen={isDefaultsModalOpen}
        onClose={() => setIsDefaultsModalOpen(false)}
      />
    </div>
  );
};

export default PropertiesPanel;